import React from "react";
import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { CheckRounded, CloseRounded } from "@mui/icons-material";

interface LocationCardSearchChipProps {
  /**
   * Whether the location is currently visible in buyer search results.
   * Maps to `ILocationData.isVisible`.
   */
  isVisible: boolean;
  /** Optional sub-label shown below the chip (e.g. "Pending Approval"). */
  subLabel?: string;
}

const ChipRoot = styled(Box, {
  shouldForwardProp: (prop) => prop !== "ownerVisible",
})<{ ownerVisible: boolean }>(({ ownerVisible }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  fontSize: "0.7857rem",  // 11px at 14px root
  fontWeight: 700,
  padding: "2px 8px",
  borderRadius: 3,
  textTransform: "uppercase",
  letterSpacing: "0.4px",
  ...(ownerVisible
    ? { background: "#e6f9ee", color: "#2e9e59", border: "1px solid #a8e6be" }
    : { background: "#fde8e8", color: "#c04040", border: "1px solid #f5b8b8" }),
}));

const SubLabel = styled(Box)({
  fontSize: "0.7857rem",  // 11px at 14px root
  color: "#5E7782",
  marginTop: 2,
});

/**
 * "Visible" / "Not Visible" pill indicating whether the location
 * appears in buyer search results.
 *
 * - Pass `isVisible={location.isVisible}` — this is independent of `status`
 *   (a Pending and a Draft location are both not visible, for different reasons).
 * - Use `subLabel` to explain why (e.g. "Pending Approval", "Missing Required Fields").
 */
const LocationCardSearchChip = ({ isVisible, subLabel }: LocationCardSearchChipProps) => (
  <Box>
    <ChipRoot ownerVisible={isVisible}>
      {isVisible
        ? <CheckRounded sx={{ fontSize: "0.7857rem" }} aria-hidden="true" />
        : <CloseRounded sx={{ fontSize: "0.7857rem" }} aria-hidden="true" />
      }
      {isVisible ? "Visible" : "Not Visible"}
    </ChipRoot>
    {subLabel && <SubLabel>{subLabel}</SubLabel>}
  </Box>
);

export default LocationCardSearchChip;

